import { useContext, useEffect, useMemo,useRef, useState } from 'react';

import ArtworkDataContext from '@/context/ArtworkDataContext';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
// Temporarily hidden with the sticker button in JSX below; restore together when re-enabling stickers.
// import StickerIcon from '@/shared-components/icons/components/actions/StickerIcon';
import UploadArrowUp from '@/shared-components/icons/components/actions/UploadArrowUp';
import ActionRejectedPopup from '@/shared-components/popups/components/ActionRejectedPopup';
import StickerMenu from '@/shared-components/stickers/StickerMenu';
import useGalleryStickers from '@/shared-components/stickers/useGalleryStickers';
import { getStickerId } from '@/shared-components/stickers/utils';
import { canAccessGalleryPermission } from '@/utils/galleryPermissions';

import './styles/ThoughtInput.css';

export default function ThoughtInput({ reply, setReply, setMention, publishThought }) {
  const t = useTranslations('common.shared.artwork_view.socials.thoughts');
  const { artworkData } = useContext(ArtworkDataContext);
  const privateActionHandler = useProtectAction({ typeOfProtection: "USER_ACCOUNT_REQUIRED" });
  const textarea = useRef();
  const { state, stateHandler } = useStateHandler();
  const [isStickerPickerOpen, setIsStickerPickerOpen] = useState(false);
  const [showActionRejectedPopup, setShowActionRejectedPopup] = useState(false);
  const [isTextareaExpanded, setIsTextareaExpanded] = useState(false);
  const isGalleryMember = state.user_session?.user_type !== "gallery"
    && Boolean(state.user_session?.gallery_id);
  const canWriteThoughts = canAccessGalleryPermission(
    state.user_session,
    "thoughts",
    state.user_session?.gallery_id
  );

  // Resolve gallery to fetch its stickers
  const galleryCandidates = useMemo(
    () => [
      artworkData?.artwork_gallery,
      artworkData?.artwork_about?.artwork_gallery,
      artworkData?.artwork_about?.artwork_gallery_artist?.artist_gallery,
      artworkData?.artwork_gallery_id,
      artworkData?.artwork_about?.artwork_gallery_id,
      artworkData?.artwork_about?.artwork_gallery?.gallery,
    ],
    [artworkData]
  );
  const { galleryId, stickers, stickersLoading } = useGalleryStickers(galleryCandidates);
  const canOpenStickerPicker = Boolean(galleryId || stickersLoading || (Array.isArray(stickers) && stickers.length > 0));
  useEffect(()=>{
    if(reply) {
      textarea.current.value = `@${reply.thought_creator.user_username} `;
      textarea.current.focus();
    } else {
      textarea.current.value = "";
    }

    resizeTextarea();
  }, [reply]);

  function submitThought() {
    if (isGalleryMember && !canWriteThoughts) {
      setShowActionRejectedPopup(true);
      return;
    }
    const newThoughtText = textarea.current.value.trim();

    if(newThoughtText.length === 0) return;

    publishThought(newThoughtText, reply);

    textarea.current.value = "";
    resizeTextarea();
    setMention(null);
    setReply(null);
  }

  function resizeTextarea() {
    if (!textarea.current) return;

    textarea.current.setAttribute('rows', '1');
    const textareaStyles = window.getComputedStyle(textarea.current);
    const lineHeight = parseFloat(textareaStyles.lineHeight) || 20;
    const verticalPadding = (parseFloat(textareaStyles.paddingTop) || 0) + (parseFloat(textareaStyles.paddingBottom) || 0);
    const contentHeight = Math.max(0, textarea.current.scrollHeight - verticalPadding);
    const numRows = Math.max(1, Math.ceil(contentHeight / lineHeight));
    textarea.current.setAttribute('rows', numRows);
    setIsTextareaExpanded(numRows > 1);
  }

  function getCurrentWord() {
    const text = textarea.current.value;
    const cursorPosition = textarea.current.selectionStart;
    let start = cursorPosition - 1;
    while (start >= 0 && text[start] !== ' ' && text[start] !== '\n') {
      start--;
    }
    start++;
    let end = cursorPosition;
    while (end < text.length && text[end] !== ' ' && text[end] !== '\n') {
      end++;
    }
    return { value: text.substring(start, end), start, end };
  }

  function onInput() {
    resizeTextarea();

    const currentWord = getCurrentWord();
    const mentionRegex = /^@[a-zA-Z][a-zA-Z0-9_]*$/;

    if(mentionRegex.test(currentWord.value) && currentWord.value.length > 3) {
      setMention({ ...currentWord, value: currentWord.value.substring(1) });
    } else {
      setMention(null);
    }
  }

  function submitSticker(sticker) {
    if (isGalleryMember && !canWriteThoughts) {
      setShowActionRejectedPopup(true);
      return;
    }
    const stickerId = getStickerId(sticker);
    if (!stickerId) return;
    privateActionHandler(() => {
      // Allow publishThought to accept an object payload (text or stickerId)
      publishThought({ stickerId }, reply);
      setMention(null);
      setReply(null);
    });
  }

  useEffect(() => {
    if (!canOpenStickerPicker && isStickerPickerOpen) setIsStickerPickerOpen(false);
  }, [canOpenStickerPicker, isStickerPickerOpen]);

  return (
    <>
      <form onSubmit={e=>{e.preventDefault(); stateHandler.set("unregisteredUser", "guest_thought"); privateActionHandler(submitThought);}} className="artwork-view-thoughts__input-container">
        <div className="artwork-view-thoughts__textarea-outter-container">
          <div className={`artwork-view-thoughts__textarea-container ${isTextareaExpanded ? 'is-expanded' : ''}`}>
            <textarea
              placeholder={t('placeholder')}
              className="artwork-view-thoughts__textarea remove-scrollbar"
              rows="1"
              onInput={onInput}
              ref={textarea}
              spellCheck={false}
              id={`artwork_${artworkData._id}_thought_input`}
            />

            <div className="artwork-view-thoughts__actions">
              {/* Temporarily hidden: restore this sticker button block when sticker input is re-enabled. */}
              {/*
              <button
                type="button"
                className="artwork-view-thoughts__sticker-button"
                onClick={() => canOpenStickerPicker && setIsStickerPickerOpen(true)}
                aria-label={t('open_sticker_picker')}
                disabled={!canOpenStickerPicker}
              >
                <StickerIcon />
              </button>
              */}
              <button type="submit" className="artwork-view-thoughts__publish-thought">
                <div className='artwork-view-thoughts__publish-thought-circle'>
                  <UploadArrowUp/>
                </div>
              </button>
            </div>
          </div>
        </div>
      </form>

      {canOpenStickerPicker && (
        <StickerMenu
          open={isStickerPickerOpen}
          close={() => setIsStickerPickerOpen(false)}
          stickers={stickers}
          isLoading={stickersLoading}
          onSelectSticker={submitSticker}
        />
      )}

      <ActionRejectedPopup
        open={showActionRejectedPopup}
        close={() => setShowActionRejectedPopup(false)}
        description={t('permission_denied')}
      />
    </>
  );
}
